// Display-only helpers. Authorization and calculations stay on the server.
export const ACTIVE = new Set(['accepted', 'planning', 'executing', 'explaining']);
export const progress = { accepted: 'Queued for analysis', planning: 'Interpreting your question', executing: 'Querying the selected data', explaining: 'Preparing your answer' };
const metricLabels = { ctr:'Click-through rate', cpc:'Cost per click', cpm:'Cost per thousand impressions', ctor:'Click-to-open rate', 'crm-sales':'CRM sales', 'crm-email':'CRM email' };
export const label = text => metricLabels[text] || String(text || '').replaceAll('_', ' ');
export const format = value => value == null ? 'Unavailable' : Number(value).toLocaleString(undefined,
  Number(value)!==0 && Math.abs(Number(value))<0.01 ? {maximumSignificantDigits:6} : {maximumFractionDigits:6});
export const exactValue = (fact, field) => fact.exact?.[field] ?? (fact[field] == null ? 'Unavailable' : String(fact[field]));
export const changeUnit = fact => fact.unit==='percent' ? 'percentage points' : fact.unit;
export function resultPeriod(turn) {
  const periods=[...new Set((turn.answer?.results || []).map(r=>r.query.from+' to '+r.query.to))];
  return periods.length ? periods.join(' · ') : periodText(turn.context);
}
export function sameContext(a,b) {
  const pick = x => x ? [x.page_id,x.dataset,x.date_range,Object.entries(x.applied_filters || {}).sort()] : null;
  return JSON.stringify(pick(a))===JSON.stringify(pick(b));
}
export const chartField = spec => ['current','change','change_percent'].includes(spec.field) ? spec.field : 'current';
export const chartLabel = spec => label(spec.metric) + (chartField(spec)==='current' ? '' : chartField(spec)==='change' ? ' change' : ' relative change');
export function rankingText(query) {
  if (query?.group_by?.some(d=>['day','week','month'].includes(d))) return 'Chronological';
  const o=query?.order;
  return o ? label(o.metric)+' / '+label(o.field)+' / '+(o.direction==='asc'?'lowest first':'highest first') : 'Not specified';
}
export function chartSeries(spec,facts) {
  const rows=spec.evidence_ids.map(id=>facts.find(f=>f.id===id)).filter(Boolean);
  const time=spec.time_dimension || ['day','week','month'].find(k=>rows.some(f=>f.dimensions?.[k]));
  if (!time) return {rows,time:null,dates:[],series:[]};
  const bucket = value => {
    const d=new Date(value+'T00:00:00Z');
    if (!Number.isFinite(+d)) return null;
    if (time==='month') d.setUTCDate(1);
    if (time==='week') d.setUTCDate(d.getUTCDate()-((d.getUTCDay()+6)%7));
    return d.toISOString().slice(0,10);
  };
  const groups=new Map();
  rows.forEach(row=>{
    const dimensions=Object.entries(row.dimensions || {}).filter(([k])=>k!==time).sort();
    const key=JSON.stringify(dimensions), day=bucket(row.dimensions?.[time] || row.segment);
    if (!day) return;
    if (!groups.has(key)) groups.set(key,{key,label:dimensions.map(([k,v])=>label(k)+': '+v).join(' / ') || label(spec.metric),points:new Map()});
    groups.get(key).points.set(day,row);
  });
  const observed=[...new Set([...groups.values()].flatMap(g=>[...g.points.keys()]))].sort();
  const start=bucket(spec.from || observed[0]), end=bucket(spec.to || observed.at(-1));
  const dates=[];
  if (start && end) for(let d=new Date(start+'T00:00:00Z'); d.toISOString().slice(0,10)<=end && dates.length<3662;) {
    dates.push(d.toISOString().slice(0,10));
    if(time==='month') d.setUTCMonth(d.getUTCMonth()+1); else d.setUTCDate(d.getUTCDate()+(time==='week'?7:1));
  }
  return {rows,time,dates,series:[...groups.values()].map(g=>({...g,points:dates.map(d=>g.points.get(d) || null)}))};
}
export function evidenceRows(turn) {
  const results=turn.answer?.results || [];
  return (turn.answer?.facts || []).map(f=>{
    const r=results.find(r=>r.evidence_ids?.includes(f.id) || r.facts?.some(x=>x.id===f.id)) || results.find(r=>r.query.dataset===f.dataset);
    return {Fact:f.id,Source:label(f.dataset),Segment:f.segment,Metric:label(f.metric),Unit:f.unit,
      Current:exactValue(f,'current'),Previous:exactValue(f,'previous'),Change:exactValue(f,'change'),
      'Change unit':changeUnit(f),'Relative change (%)':exactValue(f,'change_percent'),Calculation:f.calculation,
      From:r?.query.from || '',To:r?.query.to || '',Comparison:r?.query.prior?.join(' to ') || '',
      Filters:filtersText(r?.query.filters),'Retrieved at':r?.retrieved_at || '',
      'Semantic version':turn.answer?.semantic_version || '',Observations:f.observations ?? '',
      'Coverage days':f.coverage_days ?? '',
      'Comparison available':f.comparison_available == null ? 'Unknown' : String(f.comparison_available),
      'Component sums':JSON.stringify(f.components || {}),'Ranking':rankingText(r?.query),
      'Truncated groups':r?.truncated == null ? 'Unknown' : String(r.truncated),
      'Source coverage':JSON.stringify(r?.coverage || {}),
      'Limitations':(turn.answer?.limitations || []).join(' | ')};
  });
}
export function csvCell(value) {
  let text=String(value ?? '');
  if (/^[\s]*[=+@-]/.test(text)) text="'"+text;
  return '"'+text.replaceAll('"','""')+'"';
}
export function evidenceCsv(turn) {
  const rows=evidenceRows(turn), columns=Object.keys(rows[0] || {});
  return [columns,...rows.map(r=>columns.map(k=>r[k]))].map(row=>row.map(csvCell).join(',')).join('\r\n');
}
export function answerText(turn) {
  return [turn.question,turn.answer?.summary,...(turn.answer?.observations || []).map(o=>o.text),
    'Period: '+resultPeriod(turn),'Scope: '+scopeName(turn.scope),
    ...(turn.answer?.results || []).map(r=>label(r.query.dataset)+' · '+filtersText(r.query.filters)+' · Retrieved '+r.retrieved_at),
    'Semantic version: '+(turn.answer?.semantic_version || 'Unavailable'),...(turn.answer?.limitations || [])].filter(Boolean).join('\n\n');
}
export const scopeName = value => value === 'page' ? 'Current Page' : value === 'module' ? 'Current Module' : 'Across Orbit';
export const pageName = page => !page ? 'No page selected' : page.page_id === 'overview' ? 'Overview' : page.page_id === 'paid-media' ? 'Campaign performance' : page.dataset === 'crm-email' ? 'CRM email' : 'CRM sales';
export const filtersText = filters => Object.entries(filters || {}).map(([key, values]) => label(key) + ': ' + values.join(', ')).join(' · ') || 'All page segments';
export const periodText = page => page?.date_range ? page.date_range.from + ' to ' + page.date_range.to : 'No reporting period';
export function startersFor(page, datasets = {}) {
  if (!page) return [];
  const dataset = page.page_id === 'paid-media' ? 'campaign' : page.dataset;
  const metrics = datasets[dataset]?.metrics || [];
  const result = [{ icon: 'chart', title: 'See the bigger picture', detail: 'Summarize the selected period', question: 'Summarize performance for the selected dates.' }];
  if (metrics.includes('spend')) result.push({ icon: 'source', title: 'Explore investment', detail: 'Review campaign spend', question: 'Show spend by campaign for the selected dates.' });
  if (metrics.includes('ctr')) result.push({ icon: 'info', title: 'Understand engagement', detail: 'Explore click-through rate', question: 'Explain the click-through rate for the selected dates.' });
  if (metrics.includes('revenue')) result.push({ icon: 'source', title: 'Explore revenue', detail: 'Review revenue over time', question: 'Show the revenue trend for the selected dates.' });
  if (metrics.includes('clicks') && !metrics.includes('ctr')) result.push({ icon: 'info', title: 'Understand engagement', detail: 'Review clicks over time', question: 'Show clicks over the selected dates.' });
  if (!dataset && Object.keys(datasets).length) result.push({ icon: 'source', title: 'Understand the measures', detail: 'Ask for the calculation behind a KPI', question: 'Which metrics can I analyze on this page?' });
  return result.slice(0, 3);
}

export function resultFilters(turn) {
  const results=turn.answer?.results || [];
  return results.length ? [...new Set(results.map(r=>label(r.query.dataset)+': '+filtersText(r.query.filters)))].join(' · ') : filtersText(turn.context?.applied_filters);
}
